import { useCallback } from "react";
import cn from "classnames";

import { translate } from "@ascendnow/shared";

import AscCheckBox from "../AscInputFields/AscCheckBox/AscCheckBox";
import AscAdminPagination from "../AscAdminPagination/AscAdminPagination";
import { AscTableSorter } from "./AscTableSorter";
import { AscTableActions } from "./AscTableActions";
import { AscTableResponsiveProps } from "./AscTableResponsive.type";
import styles from "./AscTableResponsive.module.scss";
import AscEmptyList from "../AscEmptyList/AscEmptyList";

export default function AscTableResponsive<T>({
  title,
  columns,
  data,
  rowKey,
  className,
  actions,
  totalPage = 0,
  showPerPage = true,
  rowSelection,
  columnSelection,
  tableWrapperClassName,
  titleClassName,
  columnBodyClassName,
  columnHeaderClassName,
  emptyCard = null,
  ...props
}: AscTableResponsiveProps<T>) {
  const handleSelectAllRows = useCallback(() => {
    if (rowSelection?.selectedRows?.length !== data?.length)
      rowSelection?.onChange(data);
    else rowSelection?.onChange([]);
  }, [data, rowKey, rowSelection]);

  const handleSelectedRows = useCallback(
    (el: T) => {
      const elementSelectedBefore = rowSelection?.selectedRows.find(
        (selectedRow: T) => selectedRow[rowKey] === el[rowKey]
      );
      if (rowSelection?.onRowSelect) {
        rowSelection?.onRowSelect(el);
        return;
      } else if (elementSelectedBefore) {
        const filteredRowSelection =
          rowSelection?.selectedRows?.filter(
            (selectedRow: T) => selectedRow[rowKey] !== (el[rowKey] as string)
          ) || [];
        rowSelection?.onChange(filteredRowSelection);
      } else rowSelection?.onChange([...rowSelection.selectedRows, el]);
    },
    [rowKey, rowSelection]
  );

  const handleSelectedColumns = useCallback(
    (el: keyof T) => {
      if (columnSelection?.selectedColumns[el]) {
        columnSelection?.onChange({
          ...columnSelection?.selectedColumns,
          [el]: false,
        });
      } else {
        columnSelection?.onChange({
          ...columnSelection?.selectedColumns,
          [el]: true,
        });
      }
    },
    [columnSelection]
  );

  return (
    <div
      data-testid="asc-table-responsive"
      className={cn(styles.ground, className)}
    >
      {title && (
        <span className={cn(styles.title, titleClassName)}>{title}</span>
      )}
      <div className={cn(styles.tableWrapper, tableWrapperClassName)}>
        {data && Array.isArray(data) && data.length > 0 ? (
          <table className={styles.table} {...props}>
            <thead className={styles.tableHead}>
              <tr className={styles.tableHeadRow}>
                {rowSelection && !rowSelection?.hidden && (
                  <th className={styles.tableCheckbox}>
                    <AscCheckBox
                      onChange={handleSelectAllRows}
                      checked={
                        rowSelection?.selectedRows?.length === data?.length
                      }
                    />
                  </th>
                )}
                {columns.map((column, index: number) => (
                  <th
                    key={index}
                    className={cn(
                      styles.tableHeadTh,
                      columnHeaderClassName,
                      column.className
                    )}
                    style={{
                      minWidth: column.width,
                      maxWidth: column.width,
                      width: column.width,
                      display: column.hidden ? "none" : "table-cell",
                      ...(column?.style ? column?.style : {}),
                    }}
                  >
                    <div
                      className={cn(
                        styles.tableHeadThWrapper,
                        column.align &&
                          styles[`tableHeadThWrapper--${column.align}`]
                      )}
                    >
                      {columnSelection &&
                        !columnSelection?.hidden &&
                        column?.selectable && (
                          <AscCheckBox
                            onChange={() =>
                              handleSelectedColumns(column?.dataIndex)
                            }
                            checked={
                              columnSelection?.selectedColumns?.[
                                column.dataIndex
                              ]
                            }
                          />
                        )}
                      <span className={styles.tableHeadTitle}>
                        {column.title}
                      </span>
                      {column.sort?.exist && (
                        <AscTableSorter
                          index={
                            column.sort?.by
                              ? column.sort?.by
                              : (column.dataIndex as string)
                          }
                        />
                      )}
                    </div>
                  </th>
                ))}
                {actions && (
                  <th className={cn(styles.tableHeadMenu)}>
                    {translate("action")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {data.map((el) => (
                <tr
                  key={el[rowKey] as string | number}
                  className={styles.tableBodyRow}
                >
                  {rowSelection && !rowSelection?.hidden && (
                    <td className={styles.tableCheckbox}>
                      <AscCheckBox
                        onChange={() => handleSelectedRows(el)}
                        checked={
                          rowSelection?.selectedRows?.filter(
                            (selectedRow) => selectedRow[rowKey] === el[rowKey]
                          ).length > 0
                        }
                      />
                    </td>
                  )}
                  {columns.map((column, index: number) => {
                    const columnData = el[column.dataIndex];
                    return (
                      <td
                        key={index}
                        className={cn(
                          styles.tableBodyTd,
                          columnBodyClassName,
                          column.align &&
                            styles[`tableBodyTd--${column.align}`],
                          column.className
                        )}
                        style={{
                          minWidth: column.width,
                          maxWidth: column.width,
                          width: column.width,
                          display: column.hidden ? "none" : "table-cell",
                          ...(column?.style ? column?.style : {}),
                        }}
                      >
                        {column.render
                          ? column.render(columnData, el)
                          : String(columnData)}
                      </td>
                    );
                  })}
                  {actions && actions(el).length > 0 && (
                    <td className={cn(styles.tableBodyTdMenu)}>
                      <AscTableActions actions={actions?.(el)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : emptyCard ? (
          emptyCard
        ) : (
          <AscEmptyList title={translate("noData")} className={styles.noData} />
        )}
      </div>
      {totalPage > 0 && (
        <div className={styles.tablePagination}>
          <AscAdminPagination pageCount={totalPage} showPerPage={showPerPage} />
        </div>
      )}
    </div>
  );
}
